import queryClient from 'common/utils/QueryClient';
import { useEventEmitter, useMutation, useSession } from 'common/hooks';
import { UseMutationOptions } from 'common/types/Api.type';
import { SkillEndorsementEventData } from 'common/types/Event-Data.type';
import { deleteEndorsedSkillApi } from '../profiles.service';
import { MySkillEndorsementResponse, SkillEndorsementsResponse } from 'features/skills';
import { deleteEndorsedSkill } from '../helpers/endorsed-skills';

export const useDeleteEndorsedSkill = (
  profileId: string,
  skillId: string,
  options: UseMutationOptions = {},
) => {
  const { account: user } = useSession();
  const { emit } = useEventEmitter<SkillEndorsementEventData>('SkillUnEndorsement');

  return useMutation(() => deleteEndorsedSkillApi(profileId, skillId), {
    showSuccessSnackbar: true,
    onSuccess: async () => {
      const listKey = ['profile', 'skill-endorsements', profileId];
      const detailKey = [...listKey, skillId];
      const myEndorsementKey = ['my-skill-endorsement', profileId, skillId];
      const updateCache = (
        current?: SkillEndorsementsResponse,
      ): SkillEndorsementsResponse => {
        if (!current?.data?.endorsedSkills) {
          return { data: { endorsedSkills: [] } };
        }
        return {
          ...current,
          data: {
            ...current.data,
            endorsedSkills: deleteEndorsedSkill(
              current.data.endorsedSkills,
              skillId,
              undefined,
              user?.profileId,
            ),
          },
        };
      };

      queryClient.setQueryData<SkillEndorsementsResponse>(detailKey, updateCache);
      queryClient.setQueryData<SkillEndorsementsResponse>(listKey, updateCache);
      queryClient.setQueryData<MySkillEndorsementResponse>(myEndorsementKey, (current) => ({
        ...current,
        data: { endorsement: null },
      }));
      await Promise.all([
        queryClient.invalidateQueries(myEndorsementKey),
        queryClient.invalidateQueries(['skill-endorsers', profileId, skillId]),
      ]);

      const updatedEndorsementDetail = queryClient.getQueryData(
        detailKey,
      ) as SkillEndorsementsResponse;

      emit({
        currentOrganization: user?.organizationId,
        profileId,
        skillId,
        data: updatedEndorsementDetail?.data?.endorsedSkills[0],
      });
    },
    ...options,
  });
};
